import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Form, Input, InputNumber, Select, Switch, Button, Row, Col } from '@/shared/ui/primitives';
import { CitySelect, useGeoDistrictOptions } from '@/shared/ui/entity-selects';
import { useTranslation } from 'react-i18next';
import { propertyAmenityApi } from '@/features/property-amenities/api/propertyAmenity.api';
import { nationalityApi } from '@/features/nationalities/api/nationality.api';

const STATUS_VALUES = ['DRAFT', 'PUBLISHED', 'ARCHIVED', 'REJECTED'];
const SMOKING_VALUES = ['SMOKER', 'NON_SMOKER', 'NO_PREFERENCE'];
const RENT_TYPE_VALUES = ['MONTHLY', 'YEARLY'];
const ROOM_TYPE_VALUES = ['PRIVATE_BATHROOM', 'SHARED_BATHROOM', 'BOTH'];
const STAY_VALUES = ['NOT_SPECIFIED', 'UNLIMITED', 'ONE_MONTH', 'TWO_MONTHS', 'THREE_MONTHS', 'FOUR_MONTHS', 'FIVE_MONTHS', 'SIX_MONTHS', 'SEVEN_MONTHS', 'EIGHT_MONTHS', 'NINE_MONTHS', 'TEN_MONTHS', 'ELEVEN_MONTHS'];
const DAYS_VALUES = ['ALL_WEEK', 'SUNDAY_TO_THURSDAY', 'WEEKENDS_ONLY'];
const INTEREST_VALUES = ['SPORTS', 'MUSIC', 'MOVIES', 'TRAVEL', 'GAMING', 'COOKING', 'READING'];
const GENDER_VALUES = ['MALE', 'FEMALE', 'NO_PREFERENCE'];
const OCCUPATION_VALUES = ['STUDENT', 'EMPLOYEE', 'NO_PREFERENCE'];
const LANGUAGE_VALUES = ['ENGLISH', 'ARABIC', 'ARABIC_AND_ENGLISH'];

export default function RoomWantedForm({ initialData, onSubmit, isLoading }: any) {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const statusOptions = STATUS_VALUES.map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const smokingOptions = SMOKING_VALUES.map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const rentTypeOptions = RENT_TYPE_VALUES.map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const roomTypeOptions = ROOM_TYPE_VALUES.map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const stayOptions = STAY_VALUES.map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const daysOptions = DAYS_VALUES.map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const interestOptions = INTEREST_VALUES.map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const genderOptions = GENDER_VALUES.map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const occupationOptions = OCCUPATION_VALUES.map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const languageOptions = LANGUAGE_VALUES.map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const selectedCityId = Form.useWatch('cityId', form);
  const { options: districtOptions, isLoading: districtsLoading } = useGeoDistrictOptions(selectedCityId);
  const { data: amenitiesData } = useQuery({
    queryKey: ['room-wanted-form', 'amenities'],
    queryFn: () => propertyAmenityApi.list({ page: 0, size: 100, sort: ['id,asc'] }),
  });
  const { data: nationalitiesData } = useQuery({
    queryKey: ['room-wanted-form', 'nationalities'],
    queryFn: () => nationalityApi.list({ page: 0, size: 100, sort: ['id,asc'] }),
  });
  const amenityOptions = (amenitiesData?.content ?? []).map((a: any) => ({ value: a.id, label: a.nameEn || a.nameAr || `#${a.id}` }));
  const nationalityOptions = (nationalitiesData?.content ?? []).map((n: any) => ({ value: n.id, label: n.countryNameEn || n.countryNameAr || `#${n.id}` }));

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        title: initialData.title || '',
        description: initialData.description || '',
        rejectReason: initialData.rejectReason || '',
        status: initialData.status || undefined,
        budget: initialData.budget ?? undefined,
        smokingPreference: initialData.smokingPreference || undefined,
        rentType: initialData.rentType || undefined,
        roomType: initialData.roomType || undefined,
        daysAvailableOption: initialData.daysAvailableOption || undefined,
        minimumStayOption: initialData.minimumStayOption || undefined,
        maximumStayOption: initialData.maximumStayOption || undefined,
        availableFrom: toDateTimeLocal(initialData.availableFrom),
        isFeatured: initialData.isFeatured ?? false,
        hideExactLocation: initialData.hideExactLocation ?? false,
        showPhoneNumber: initialData.showPhoneNumber ?? true,
        showWhatsappContact: initialData.showWhatsappContact ?? true,
        showOwnerInfo: initialData.showOwnerInfo ?? true,
        showInAppMessaging: initialData.showInAppMessaging ?? true,
        isSelectAllDistricts: initialData.isSelectAllDistricts ?? false,
        phonePrefix: initialData.phonePrefix || '',
        phoneNumber: initialData.phoneNumber || '',
        phoneCountryCode: initialData.phoneCountryCode || '',
        cityId: initialData.cityId ?? undefined,
        selectedDistricts: initialData.selectedDistricts ?? [],
        interests: initialData.interests ?? [],
        amenities: initialData.amenities?.map((a: any) => a.id) ?? [],
        ...prefsToFields('preferredRoommates', initialData.preferredRoommates),
      });
    }
  }, [initialData, form]);

  const handleFinish = (values: any) => {
    // Only send non-empty fields (preserve original payload logic)
    const payload: Record<string, unknown> = {};
    if (values.title) payload.title = values.title;
    if (values.description) payload.description = values.description;
    if (values.rejectReason) payload.rejectReason = values.rejectReason;
    if (values.status) payload.status = values.status;
    if (values.budget != null) payload.budget = Number(values.budget);
    if (values.smokingPreference) payload.smokingPreference = values.smokingPreference;
    if (values.rentType) payload.rentType = values.rentType;
    if (values.roomType) payload.roomType = values.roomType;
    if (values.daysAvailableOption) payload.daysAvailableOption = values.daysAvailableOption;
    if (values.minimumStayOption) payload.minimumStayOption = values.minimumStayOption;
    if (values.maximumStayOption) payload.maximumStayOption = values.maximumStayOption;
    if (values.availableFrom) payload.availableFrom = new Date(values.availableFrom).toISOString();
    payload.isFeatured = values.isFeatured;
    payload.hideExactLocation = values.hideExactLocation;
    payload.showPhoneNumber = values.showPhoneNumber;
    payload.showWhatsappContact = values.showWhatsappContact;
    payload.showOwnerInfo = values.showOwnerInfo;
    payload.showInAppMessaging = values.showInAppMessaging;
    payload.isSelectAllDistricts = values.isSelectAllDistricts;
    if (values.phonePrefix) payload.phonePrefix = values.phonePrefix;
    if (values.phoneNumber) payload.phoneNumber = values.phoneNumber;
    if (values.phoneCountryCode) payload.phoneCountryCode = values.phoneCountryCode;
    if (values.cityId != null) payload.cityId = Number(values.cityId);
    if (Array.isArray(values.selectedDistricts)) payload.selectedDistricts = values.selectedDistricts.map(Number);
    if (Array.isArray(values.interests)) payload.interests = values.interests;
    if (Array.isArray(values.amenities)) payload.amenities = values.amenities.map(Number);
    const preferredRoommates = collectPrefs(values, 'preferredRoommates');
    if (preferredRoommates) payload.preferredRoommates = preferredRoommates;
    onSubmit(payload);
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} style={{ maxWidth: 600 }}>
      <Form.Item label={t('common.title')} name="title">
        <Input />
      </Form.Item>

      <Form.Item label={t('roomWanted.description')} name="description">
        <Input.TextArea rows={3} />
      </Form.Item>

      <Form.Item label={t('roomWanted.rejectReason')} name="rejectReason">
        <Input.TextArea rows={2} />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t('roomWanted.status')} name="status">
            <Select allowClear options={statusOptions} placeholder="—" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('roomWanted.budget')} name="budget">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label={t('roomWanted.smoking')} name="smokingPreference">
            <Select allowClear options={smokingOptions} placeholder="—" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('roomWanted.rentType')} name="rentType">
            <Select allowClear options={rentTypeOptions} placeholder="—" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('roomWanted.roomType')} name="roomType">
            <Select allowClear options={roomTypeOptions} placeholder="—" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item label={t('roomWanted.cityId')} name="cityId">
            <CitySelect
              placeholder={t('roomWanted.cityId')}
              onChange={(value: any) => form.setFieldsValue({
                cityId: value,
                selectedDistricts: [],
              })}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label={t('roomWanted.districts')} name="selectedDistricts">
        <Select
          mode="multiple"
          allowClear
          showSearch
          options={districtOptions}
          disabled={!selectedCityId}
          loading={districtsLoading}
        />
      </Form.Item>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label={t('roomWanted.daysAvailable')} name="daysAvailableOption">
            <Select allowClear options={daysOptions} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('roomWanted.minStay')} name="minimumStayOption">
            <Select allowClear options={stayOptions} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('roomWanted.maxStay')} name="maximumStayOption">
            <Select allowClear options={stayOptions} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('roomWanted.availableFrom')} name="availableFrom">
            <Input type="datetime-local" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label={t('roomWanted.amenities')} name="amenities">
        <Select mode="multiple" allowClear showSearch options={amenityOptions} />
      </Form.Item>
      <Form.Item label={t('roomWanted.interests')} name="interests">
        <Select mode="multiple" allowClear options={interestOptions} />
      </Form.Item>

      <Row gutter={16}>
        <Col span={6}><Form.Item label={t('roommatePreferences.maleCount')} name="preferredRoommatesMaleRoommatesCount"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
        <Col span={6}><Form.Item label={t('roommatePreferences.femaleCount')} name="preferredRoommatesFemaleRoommatesCount"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
        <Col span={6}><Form.Item label={t('roommatePreferences.minAge')} name="preferredRoommatesMinAge"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
        <Col span={6}><Form.Item label={t('roommatePreferences.maxAge')} name="preferredRoommatesMaxAge"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
        <Col span={6}><Form.Item label={t('roommatePreferences.gender')} name="preferredRoommatesGenderPreference"><Select allowClear options={genderOptions} /></Form.Item></Col>
        <Col span={6}><Form.Item label={t('roommatePreferences.occupation')} name="preferredRoommatesOccupationType"><Select allowClear options={occupationOptions} /></Form.Item></Col>
        <Col span={6}><Form.Item label={t('roommatePreferences.language')} name="preferredRoommatesLanguagePreference"><Select allowClear options={languageOptions} /></Form.Item></Col>
        <Col span={6}><Form.Item label={t('roommatePreferences.nationality')} name="preferredRoommatesNationalityId"><Select allowClear showSearch options={nationalityOptions} /></Form.Item></Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label={t('roomWanted.phonePrefix')} name="phonePrefix">
            <Input autoComplete="tel-country-code" data-allstak-mask="true" data-sensitive="true" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('roomWanted.phoneNumber')} name="phoneNumber">
            <Input autoComplete="tel-national" data-allstak-mask="true" data-sensitive="true" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('roomWanted.phoneCountryCode')} name="phoneCountryCode">
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col>
          <Form.Item label={t('roomWanted.featured')} name="isFeatured" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label={t('roomWanted.showPhone')} name="showPhoneNumber" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label={t('roomWanted.showWhatsapp')} name="showWhatsappContact" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label={t('roomWanted.showOwnerInfo')} name="showOwnerInfo" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label={t('roomWanted.showInAppMessaging')} name="showInAppMessaging" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label={t('roomWanted.hideLocation')} name="hideExactLocation" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label={t('roomWanted.allDistricts')} name="isSelectAllDistricts" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isLoading}>
          {t('common.save')}
        </Button>
      </Form.Item>
    </Form>
  );
}

function toDateTimeLocal(value?: string | null) {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 16);
}

function prefsToFields(prefix: string, prefs: any) {
  if (!prefs) return {};
  return {
    [`${prefix}MaleRoommatesCount`]: prefs.maleRoommatesCount,
    [`${prefix}FemaleRoommatesCount`]: prefs.femaleRoommatesCount,
    [`${prefix}MinAge`]: prefs.minAge,
    [`${prefix}MaxAge`]: prefs.maxAge,
    [`${prefix}NationalityId`]: prefs.nationalityId,
    [`${prefix}GenderPreference`]: prefs.genderPreference,
    [`${prefix}SmokingPreference`]: prefs.smokingPreference,
    [`${prefix}OccupationType`]: prefs.occupationType,
    [`${prefix}LanguagePreference`]: prefs.languagePreference,
  };
}

function collectPrefs(values: any, prefix: string) {
  return normalizePrefs({
    maleRoommatesCount: values[`${prefix}MaleRoommatesCount`],
    femaleRoommatesCount: values[`${prefix}FemaleRoommatesCount`],
    minAge: values[`${prefix}MinAge`],
    maxAge: values[`${prefix}MaxAge`],
    nationalityId: values[`${prefix}NationalityId`],
    genderPreference: values[`${prefix}GenderPreference`],
    smokingPreference: values[`${prefix}SmokingPreference`],
    occupationType: values[`${prefix}OccupationType`],
    languagePreference: values[`${prefix}LanguagePreference`],
  });
}

function normalizePrefs(prefs: Record<string, any>) {
  if (!prefs || Object.values(prefs).every((value) => value === undefined || value === null || value === '')) return null;
  return {
    ...prefs,
    maleRoommatesCount: prefs.maleRoommatesCount != null ? Number(prefs.maleRoommatesCount) : undefined,
    femaleRoommatesCount: prefs.femaleRoommatesCount != null ? Number(prefs.femaleRoommatesCount) : undefined,
    minAge: prefs.minAge != null ? Number(prefs.minAge) : undefined,
    maxAge: prefs.maxAge != null ? Number(prefs.maxAge) : undefined,
    nationalityId: prefs.nationalityId != null ? Number(prefs.nationalityId) : undefined,
  };
}
